import React from 'react';

import Image from 'next/image';

import clsx from 'clsx';

import { useFeatures } from '@atc/features';

import {
    Button,
    ItemCard,
    List,
    Panel,
    Text,
} from 'reaxl';
import {
    InventoryListingTitle,
} from 'reaxl-listing';
import {
    InfoIcon,
    OwnerDistance,
    OwnerPhoneNumber,
    PricingV2,
} from 'reaxl-molecules';
import { WalletPaymentDetailsV2 } from 'reaxl-wallet';

import { placeholderImages } from 'axl-config';

// fetchers
import PaymentCalculationFetcher from '@/fetchers/PaymentCalculationFetcher';

function PreorderCard({
    emailOwnerProps = {},
    listing = {},
    onMyWalletClick = () => { },
    isPhoneNumberCallable,
}) {
    const {
        nds: ndsFeature,
        retail_engine: [enableRetailEngine],
    } = useFeatures([
        'nds',
        'retail_engine',
    ]);

    const {
        id,
        pricingDetail = {},
        title,
        owner,
        imageUrl,
    } = listing;

    const analyticsData = {
        inventoryId: id,
        clickType: 'preorder',
        parentId: 'newcarorder',
        listing,
    };

    const personalPaymentProps = {
        fetcher: PaymentCalculationFetcher,
        paymentsEnabled: enableRetailEngine,
        ndsFeature,
    };

    const renderMedia = (
        <div className={clsx('padding-horizontal-4', { 'bg-neutral-tertiary': !imageUrl })}>
            <div
                className={clsx('image-container', { 'bg-neutral-tertiary': !imageUrl })}
                style={{ position: 'relative' }}
            >
                <Image
                    src={imageUrl || placeholderImages.noImageAvailable}
                    fill
                    alt=""
                    style={{ objectFit: 'contain' }}
                    className={clsx({ 'padding-4': !imageUrl })}
                />
            </div>
        </div>
    );

    const msrpTooltip = 'MSRP excludes tax, title, license, delivery, processing & handling fees and optional equipment. Contact dealer for details.';

    const renderBody = (
        <div
            key="title"
            className="margin-3"
        >
            <InventoryListingTitle
                title={title}
                size={400}
            />
            <div className="display-flex align-items-start">
                <PricingV2
                    key="inventory-listing-pricing"
                    data={pricingDetail}
                    uiContext="listing"
                    orientation="portrait"
                />
                <span className="text-size-200 text-subdued-lightest display-block margin-left-2 padding-bottom-1 text-normal second-price align-self-end">
                    Starting MSRP before options
                    <InfoIcon
                        className="margin-left-1"
                        popoverId="msrp-more-info-popover"
                        placement="top"
                        infoText={msrpTooltip}
                    />
                </span>
            </div>
            <WalletPaymentDetailsV2
                listingId={id}
                isPreorder
                onMyWalletClick={onMyWalletClick}
                personalPaymentProps={personalPaymentProps}
                uiContext="ribbon"
            />
        </div>
    );

    const renderSellerPhone = ({ ...props }) => (
        <OwnerPhoneNumber
            className="display-block"
            key="listingFooterOwnerPhone"
            {...props}
        />
    );

    const ownerInfoList = [<OwnerDistance
        key={`${id}-owner-distance`}
        className="text-subdued"
        owner={owner}
        uiContext="owner"
        showIcon={false}
        showOwnerDistance
    />,
    ];

    if (!isPhoneNumberCallable && owner.phone) {
        ownerInfoList.push(
            <div
                className="text-subdued display-flex"
                data-cmp="contactInfo"
                key={`${id}-owner-phone`}
            >
                {owner.phone && renderSellerPhone({
                    phone: owner.phone,
                    isCallable: isPhoneNumberCallable,
                })}
            </div>
        );
    }

    const renderFooter = (
        <div
            className="listing-footer text-subdued-lightest text-size-200 padding-right-3 padding-left-3"
            style={{ minHeight: '83px' }}
        >
            <hr />
            <div className="text-subdued">{owner.name}</div>
            <List
                items={ownerInfoList}
                inline
            />

            <Text
                className="padding-vertical-3"
                componentClass="div"
                size={100}
            >
                For illustration purposes only. May not match exact trim or color of the vehicle shown. No obligation to purchase.
            </Text>
            <div className="display-flex flex-sm-wrap">
                <Button
                    className="margin-right-2 margin-right-sm-0"
                    block
                    bsStyle="secondary"
                    bsSize="small"
                    onClick={emailOwnerProps.onCTAClick}
                >
                    Get Price
                </Button>
                <Button
                    className="margin-top-0 margin-top-sm-2 margin-left-sm-0"
                    block
                    bsSize="small"
                    bsStyle="primary"
                    onClick={emailOwnerProps.onBuildClick}
                >
                    Custom Order
                </Button>
                {owner.phone && isPhoneNumberCallable && renderSellerPhone({
                    phone: owner.phone,
                    isCallable: isPhoneNumberCallable,
                    analyticsData,
                    uiContext: 'button',
                    buttonStyle: 'default',
                    buttonSize: 'small',
                    className: 'margin-left-2',
                    buttonLabel: 'Call',
                })}
            </div>
        </div>
    );

    return (
        <Panel
            className="inventory-listing cursor-pointer display-flex flex-column"
            data-cmp="preorderCard"
        >
            <Panel.Body className="padding-0 display-flex flex-grow-1 cursor-pointer display-flex flex-column">
                <ItemCard
                    uiContext="portrait"
                    sideBar={renderMedia}
                    body={renderBody}
                    footer={renderFooter}
                />
            </Panel.Body>
        </Panel>
    );
}

export default PreorderCard;
